import React, { useEffect } from 'react'
import { DataPickerHLPure, InteractiveDialog, Traslates } from '../../pures'
import { useTranslation } from 'react-i18next'
import { Grid } from '@material-ui/core'
import { useState } from 'react'
import { DateTime } from "luxon"
import './dialog-edit-expiration-date.css'
import { LocalStorageService } from '../../../services'
import StandardDates from '../../../utils/StandardDates.util'

/**
 * @function DialogEditExpirationDate
 * @description Dialog to edit the expiration date with validation and i18n.
 * @param {{
 *  expirationDate: string,
 *  visibleExpirationDate: boolean,
 *  validator: { limitDateValidator: (value: Date | string) => string },
 *  toggleEditExpirationDate: () => void,
 *  onUpdateExpirationDate?: (payload: { expirationDate: Date }, setForceHide: (value: boolean) => void) => Promise<void>,
 *  limitExpirationDate: string | Date,
 *  date?: string | Date
 * }} props
 * @returns {React.ReactElement}
 */
const DialogEditExpirationDate = ({
  dateLimitTypeDate,
  expirationDate,
  visibleExpirationDate,
  validator,
  toggleEditExpirationDate,
  onUpdateExpirationDate,
  date
}) => {
  const { t } = useTranslation()
  const [submittedMeta, setSubmittedMeta] = useState({ submitted: false, time: null })
  const [selectedDate, setSelectedDate] = useState(null)
  const [forceHide, setForceHide] = useState(false)
  const [errorDate, setErrorDate] = useState(true)

  useEffect(() => {
    if (visibleExpirationDate) {
      if (submittedMeta.submitted && submittedMeta.time) {
        const elapsed = Date.now() - submittedMeta.time
        const suppressionWindow = 3000
        if (elapsed < suppressionWindow) {
          setForceHide(true)
          return
        } else {
          setSubmittedMeta({ submitted: false, time: null })
          setForceHide(false)
        }
      }
    } else {
      if (!submittedMeta.submitted) setForceHide(false)
    }
  }, [visibleExpirationDate, submittedMeta])

  useEffect(() => {
    if (!visibleExpirationDate) return
    const adjust = () => {
      const input = document.querySelector('.expiration-date-dialog .dataPickerHLPure-style input')
      if (input && typeof input.value === 'string') {
        input.value = input.value.replace(/\/([a-záéíóúñ]{3,5})\./i, '/$1')
      }
    }
    adjust()
    const listener = (e) => adjust()
    const input = document.querySelector('.expiration-date-dialog .dataPickerHLPure-style input')
    if (input) input.addEventListener('blur', listener)
    return () => { if (input) input.removeEventListener('blur', listener) }
  }, [visibleExpirationDate, selectedDate])

  useEffect(() => {
    if (!visibleExpirationDate) return
    const input = document.querySelector('.expiration-date-dialog .dataPickerHLPure-style input')
    if (!input) return

    input.setAttribute('readonly', 'readonly')
    input.setAttribute('aria-readonly', 'true')
    input.setAttribute('inputmode', 'none')

    const prevent = (e) => {
      const allowedKeys = ['Tab', 'Shift', 'Control', 'Alt', 'Meta', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']
      if (e.type === 'keydown') {
        if (allowedKeys.includes(e.key)) return
      }
      e.preventDefault()
      e.stopPropagation()
    }

    input.addEventListener('keydown', prevent)
    input.addEventListener('beforeinput', prevent)
    input.addEventListener('paste', prevent)
    input.addEventListener('drop', prevent)

    return () => {
      input.removeAttribute('readonly')
      input.removeAttribute('aria-readonly')
      input.removeAttribute('inputmode')
      input.removeEventListener('keydown', prevent)
      input.removeEventListener('beforeinput', prevent)
      input.removeEventListener('paste', prevent)
      input.removeEventListener('drop', prevent)
    }
  }, [visibleExpirationDate])

  const validateDate = (event) => {
    const value = event?.value
    if (!value) {
      setSelectedDate(null)
      setErrorDate(true)
      return
    }
    const validationInput = value
    let dateIsValid = validator.limitDateValidator(validationInput) === ''
    setErrorDate(!dateIsValid)
    setSelectedDate(value)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    let maxDatePicker = new Date(dateLimitTypeDate)
    const currentDateLimit = date ? new Date(date) : null
    if (!selectedDate || (currentDateLimit && selectedDate < currentDateLimit) || selectedDate > maxDatePicker) {
      setErrorDate(true)
      return
    }
    setForceHide(true)
    setSubmittedMeta({ submitted: true, time: Date.now() })
    if (typeof toggleEditExpirationDate === 'function') {
      toggleEditExpirationDate()
    }
    try {
      await onUpdateExpirationDate?.({ expirationDate: selectedDate }, setForceHide)
    } catch (err) {
      console.error('Error updating expiration date', err)
    }
  }

  const dialogDescription = <div className='expiration-date-dialog-description'>
    <p>
      {t('dialogEditExpirationDate.youOnlyChangeTheDatiIn60days')}
      <br />
      {t('dialogEditExpirationDate.limitDateAllow')}: {' '}
      <span style={{ fontWeight: 600 }}>
        {StandardDates.fromISOToStandardDate(dateLimitTypeDate)}
      </span>
    </p>
  </div>

  return (
    <div className='expiration-date-dialog'>
      {visibleExpirationDate && !forceHide && (
        <InteractiveDialog
          title={t('dialogEditExpirationDate.titleDialog')}
          onClose={toggleEditExpirationDate}
          onSave={handleSubmit}
          cancelBtnTitle={t('dialogEditExpirationDate.buttonCancel')}
          saveBtnTitle={t('dialogEditExpirationDate.buttonUpdateDate')}
        >
          {dialogDescription}
          <Grid container xs={12} lg={12} md={12} sm={12} es={12} style={{ height: '290px' }}>
            <Grid item xs={12} lg={12} md={12} sm={12} es={12}>
              <Traslates>
                <DataPickerHLPure
                  labelDataPicker={t('dialogEditExpirationDate.labelNewLimitDate')}
                  limitDate={StandardDates.fromISODate(expirationDate).plus({ days: 1 }).toJSDate()}
                  handleChangeDate={validateDate}
                  maxDate={StandardDates.fromISODate(dateLimitTypeDate).toJSDate()}
                  formatPicker={StandardDates.STANDARD_DATE_FORMAT}
                />
                {errorDate && selectedDate && (
                  <p className='hint-message-error'>{t('dialogEditExpirationDate.error')}</p>
                )}
              </Traslates>
            </Grid>
          </Grid>
        </InteractiveDialog>
      )}
    </div>
  )
}
export { DialogEditExpirationDate as default }
